import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, defaultLanguage, supportedLanguages, translations } from '../translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations[Language];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  initialLanguage 
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Use initialLanguage if provided (from URL or server-side)
    if (initialLanguage) {
      return initialLanguage;
    }
    
    // Check if language was set in static HTML (for crawlers/SSR)
    if (typeof window !== 'undefined') {
      const staticLang = (window as any).__INITIAL_LANGUAGE__;
      if (staticLang && supportedLanguages.includes(staticLang)) {
        return staticLang;
      }
      
      // Detect browser language
      const browserLang = navigator.language || (navigator as any).userLanguage;
      const langLower = browserLang.toLowerCase();
      
      // Check for pt-BR first (full match)
      if (langLower.startsWith('pt-br') && supportedLanguages.includes('pt-BR')) {
        return 'pt-BR';
      }
      
      // Check for other languages
      const langCode = langLower.split('-')[0] as Language;
      if (supportedLanguages.includes(langCode)) {
        return langCode;
      }
    }
    
    return defaultLanguage;
  });

  // Update document language and meta tags when language changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.documentElement.lang = language;
      
      // Update meta tags
      const title = translations[language].meta.title;
      const description = translations[language].meta.description;
      
      document.title = title;
      
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
      
      // Update URL without page reload (for client-side navigation)
      const path = window.location.pathname;
      const langPath = `/${language}`;
      if (!path.startsWith(langPath) && path !== '/') {
        // Replace any language path (handles both 2-letter and pt-BR)
        const newPath = path.replace(/^\/([a-z]{2}|pt-br)(\/|$)/i, `/${language}$2`);
        if (newPath !== path) {
          window.history.replaceState({}, '', newPath);
        }
      } else if (path === '/' || !supportedLanguages.some(lang => {
        const langLower = lang.toLowerCase();
        return path.toLowerCase().startsWith(`/${langLower}`);
      })) {
        window.history.replaceState({}, '', `/${language}`);
      }
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    if (supportedLanguages.includes(lang)) {
      setLanguageState(lang);
    }
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
